import { formatDistance, formatDistanceStrict, formatISO, isAfter, isBefore, isValid, parse } from 'date-fns';
import { format, toDate, utcToZonedTime } from 'date-fns-tz';

import { UTC_TIME_ZONE } from './timezone-date-utils.variables';
import { DateLocale } from './types/date-locale.type';
import { TimeZone } from './types/timezone.type';

export const changeDateLocale = (locale: DateLocale) => (window.__timezoneDateUtilsLocaleId__ = locale);
export const changeDateTimeZone = (timeZone: TimeZone) => (window.__timezoneDateUtilsTimeZone__ = timeZone);
export const resetDateTimeZone = () => (window.__timezoneDateUtilsTimeZone__ = 'Europe/Oslo');

export const getDateLocale = () => window.__timezoneDateUtilsLocaleId__;
export const getDateTimeZone = () => window.__timezoneDateUtilsTimeZone__;

/**
 * Convert the given argument to an instance of Date with UTC.
 * Make sure that the give argument is actually a date string that should be interpreted as UTC.
 * @param dateString The value to convert
 * @returns The parsed date in the utc time zone
 */
export const toUTCDate = (dateString: string | number) => toDate(dateString, { timeZone: UTC_TIME_ZONE });

/**
 * Convert the given argument to an instance of Date with UTC.
 * Make sure that the give argument is actually a date string that should be interpreted as UTC.
 * @param dateString The value to convert
 * @returns The parsed date in the utc time zone
 */
export const toUTCDateOrUndefined = (dateString?: string | number) =>
  dateString != null ? toUTCDate(dateString) : undefined;

/**
 * Return the formatted date in ISO 8601 date string format
 * @param date The original date
 * @returns The formatted date string (in local time zone)
 */
export const toISODateString = (date: Date) =>
  isValid(date) ? formatISO(date, { representation: 'date', format: 'extended' }) : '';

/**
 * Return the formatted date in ISO 8601 date string format
 * @param date The original date
 * @returns The formatted date string (in local time zone)
 */
export const toISODateStringOrUndefined = (date?: Date | null) => (date != null ? toISODateString(date) : undefined);

/**
 * Returns false if argument is Invalid Date and true otherwise. Argument is converted to Date using `toDate`
 * @param date The date to check
 * @returns The date is valid
 */
export const isValidDate = (date: unknown): boolean => isValid(date);

/**
 * Returns false if argument is Invalid Date or nullish and true otherwise. Argument is converted to Date using `toDate`
 * @param date The date to check
 * @returns The date is valid and non nullish
 */
export const isValidDefinedDate = (date: unknown): date is Date => date != null && isValid(date);

/**
 * Returns false if argument is a invalid date string and true otherwise. Argument is converted parsed as a date and checked via `isValidDefinedDate`
 * @param dateString The date string to check
 * @param format The format of the date string (default: `yyyy-MM-dd`)
 * @returns The date string is valid and non nullish and therefore can be used as a string to create a date
 */
export const isValidDefinedDateString = (dateString: unknown, format: string = 'yyyy-MM-dd'): dateString is string => dateString != null && typeof dateString === 'string' && isValidDefinedDate(parse(dateString, format, new Date()));

/**
 * Return the formatted date string. The result vary depending on the defined time zone and locale option.
 * Date is formatted as string with time (day, month & year).
 * @param date The original date
 * @returns The formatted date string
 */
export const toZonedDateString = (date: Date) =>
  format(utcToZonedTime(date, getDateTimeZone() ?? UTC_TIME_ZONE), 'P', {
    timeZone: getDateTimeZone(),
    locale: getDateLocale(),
  });

/**
 * Return the formatted date string. The result vary depending on the defined time zone and locale option.
 * Date is formatted as string with date (day, month & year) and time (hours, minutes & seconds).
 * @param date The original date
 * @returns The formatted date string
 */
export const toZonedDateTimeString = (date: Date) =>
  format(utcToZonedTime(date, getDateTimeZone() ?? UTC_TIME_ZONE), 'Ppp', {
    /* timeZone: getDateTimeZone(), */ locale: getDateLocale(),
  });

/**
 * Return the formatted time string. The result vary depending on the defined time zone and locale option.
 * Date is formatted as string with time (hours & minutes).
 * @param date The original date
 * @returns The formatted time string
 */
export const toZonedTimeString = (date: Date) =>
  format(utcToZonedTime(date, getDateTimeZone() ?? UTC_TIME_ZONE), 'p', {
    timeZone: getDateTimeZone(),
    locale: getDateLocale(),
  });

/**
 * Return the date string in the given format. The result vary depending on the defined time zone and locale option.
 * Date is formatted as string with time (hours & minutes).
 * @param date The original date
 * @param formatTokens The string of tokens
 * @returns The formatted date string
 */
export const toZonedFormattedString = (date: Date, formatTokens: string) =>
  format(utcToZonedTime(date, getDateTimeZone() ?? UTC_TIME_ZONE), formatTokens, {
    timeZone: getDateTimeZone(),
    locale: getDateLocale(),
  });

/**
 * Return the distance between the given dates in words.
 * @param toDate The later date
 * @param fromDate The earlier date to compare with
 * @param suffix Result indicates if the first date is earlier (past) or later (future) than the second date
 * @returns The distance in words
 */
export const toZonedDistanceDateString = (toDate: Date, fromDate: Date, suffix = true) =>
  formatDistance(
    utcToZonedTime(toDate, getDateTimeZone() ?? UTC_TIME_ZONE),
    utcToZonedTime(fromDate, getDateTimeZone() ?? UTC_TIME_ZONE),
    { addSuffix: suffix, locale: getDateLocale() }
  );

export const toZonedDistanceStrictDateString = (toDate: Date, fromDate: Date, suffix = true) =>
  formatDistanceStrict(
    utcToZonedTime(toDate, getDateTimeZone() ?? UTC_TIME_ZONE),
    utcToZonedTime(fromDate, getDateTimeZone() ?? UTC_TIME_ZONE),
    { addSuffix: suffix, locale: getDateLocale() }
  );

/**
 * Is the first date before the second one?
 * @param firstDate The date that should be before the other one to return true
 * @param secondDate The date to compare with
 * @returns True if the first date is before the second date else false
 */
export const isBeforeDate = (firstDate: Date, secondDate: Date) => isBefore(firstDate, secondDate);

/**
 * Is the first date after the second one?
 * @param firstDate The date that should be after the other one to return true
 * @param secondDate The date to compare with
 * @returns True if the first date is after the second date else false
 */
export const isAfterDate = (firstDate: Date, secondDate: Date) => isAfter(firstDate, secondDate);

/**
 * ###########################################################################################################################
 * Some helper fn to handle undefined
 */

export const toZonedDateStringOrUndefined = (date: Date | undefined) => {
  if (!isValidDefinedDate(date)) return undefined;
  return toZonedDateString(date);
};

export const toZonedDateTimeStringOrUndefined = (date: Date | undefined) => {
  if (!isValidDefinedDate(date)) return undefined;
  return toZonedDateTimeString(date);
};

export const toZonedTimeStringOrUndefined = (date: Date | undefined) => {
  if (!isValidDefinedDate(date)) return undefined;
  return toZonedTimeString(date);
};

export const toZonedFormattedStringOrUndefined = (date: Date | undefined, formatTokens: string) => {
  if (!isValidDefinedDate(date)) return undefined;
  return toZonedFormattedString(date, formatTokens);
};

export const toZonedDistanceDateStringOrUndefined = (
  toDate: Date | undefined,
  fromDate: Date | undefined,
  suffix?: boolean
) => {
  if (!isValidDefinedDate(toDate)) return undefined;
  if (!isValidDefinedDate(fromDate)) return undefined;
  return toZonedDistanceDateString(toDate, fromDate, suffix);
};

export const toZonedDistanceStrictDateStringOrUndefined = (
  toDate: Date | undefined,
  fromDate: Date | undefined,
  suffix?: boolean
) => {
  if (!isValidDefinedDate(toDate)) return undefined;
  if (!isValidDefinedDate(fromDate)) return undefined;
  return toZonedDistanceStrictDateString(toDate, fromDate, suffix);
};

/**
 * ###########################################################################################################################
 */
